import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Separator } from './components/ui/separator';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Checkbox } from './components/ui/checkbox';
import { FileText, Send, Check, X, Eye, Download, Calendar, DollarSign, Building, Edit, Trash2, Mail, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import { ProposalPreview } from './components/ProposalPreview';
import { EmailTemplateLibrary } from './components/EmailTemplateLibrary';
import { BulkProposalActions } from './components/BulkProposalActions';
import { RecordContextHeader } from './components/RecordContextHeader';
import { ProposalAttachments } from './components/ProposalAttachments';
import { EmptyState } from './components/EmptyState';
import { NextStepPrompt } from './components/NextStepPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export function ProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [emailData, setEmailData] = useState({ to_email: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showWonPrompt, setShowWonPrompt] = useState(false);
  const [wonClientId, setWonClientId] = useState(null);
  // Load related entities for preview
  const [relatedData, setRelatedData] = useState({ lead: null, walkthrough: null, bid: null, companyInfo: null });

  useEffect(() => {
    if (showPreview && selectedProposal) {
      loadRelatedData();
    }
  }, [showPreview, selectedProposal]);

  const loadRelatedData = async () => {
    if (!selectedProposal) return;
    try {
      const companyInfo = await api('/api/settings/company');
      let bid = null;
      let walkthrough = null;
      let lead = null;

      if (selectedProposal.bid_id) {
        bid = await api(`/api/bids?lead_id=${selectedProposal.bid_id}`).then(bids => bids.find(b => b.id === selectedProposal.bid_id));
        if (bid && bid.walkthrough_id) {
          const walkthroughs = await api('/api/walkthroughs');
          walkthrough = walkthroughs.find(w => w.id === bid.walkthrough_id);
          if (walkthrough && walkthrough.lead_id) {
            const leads = await api('/api/leads');
            lead = leads.find(l => l.id === walkthrough.lead_id);
          }
        }
      }

      setRelatedData({ lead, walkthrough, bid, companyInfo });
    } catch (error) {
      console.error('Failed to load related data:', error);
    }
  };

  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    try {
      const data = await api('/api/proposals');
      setProposals(data || []);
    } catch (error) {
      toast.error('Failed to load proposals');
    }
  };

  // Bulk actions - must be after loadProposals definition
  const bulkActions = BulkProposalActions({ proposals, onRefresh: loadProposals });

  const handleUpdateStatus = async (proposalId, newStatus) => {
    try {
      await api(`/api/proposals/${proposalId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      toast.success(`Proposal marked as ${newStatus}`);
      loadProposals();
      if (showPreview && selectedProposal?.id === proposalId) {
        setSelectedProposal({...selectedProposal, status: newStatus});
      }
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm('Delete this proposal? This cannot be undone.')) return;
    try {
      await api(`/api/proposals/${proposalId}`, { method: 'DELETE' });
      toast.success('Proposal deleted');
      if (showPreview && selectedProposal?.id === proposalId) {
        setShowPreview(false);
        setSelectedProposal(null);
      }
      loadProposals();
    } catch (error) {
      toast.error('Failed to delete proposal');
    }
  };

  const handleDuplicate = async (proposal) => {
    if (!window.confirm('Duplicate this proposal?')) return;
    try {
      await api(`/api/bids/${proposal.bid_id}/create-proposal`, {
        method: 'POST',
        body: JSON.stringify({
          services: proposal.services,
          notes: proposal.notes + ' (Duplicate)',
          exclusions: proposal.exclusions
        })
      });
      toast.success('Proposal duplicated');
      loadProposals();
    } catch (error) {
      toast.error('Failed to duplicate proposal');
    }
  };

  const handleEdit = (proposal) => {
    setEditingProposal(proposal);
    setEditFormData({
      services: proposal.services,
      notes: proposal.notes || '',
      exclusions: proposal.exclusions || '',
      monthly_price: proposal.monthly_price
    });
  };

  const handleSaveEdit = async () => {
    if (!editingProposal) return;
    try {
      await api(`/api/proposals/${editingProposal.id}`, {
        method: 'PUT',
        body: JSON.stringify(editFormData)
      });
      toast.success('Proposal updated');
      setEditingProposal(null);
      setEditFormData({});
      loadProposals();
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const handleMarkWon = async (proposalId) => {
    if (!window.confirm('Mark this proposal as won and convert to client?')) return;
    try {
      const response = await api(`/api/proposals/${proposalId}/mark-won`, {
        method: 'POST',
        body: JSON.stringify({ create_location: false })
      });
      toast.success('Proposal won! Client created.');
      setWonClientId(response.client_id);
      setShowWonPrompt(true);
      loadProposals();
    } catch (error) {
      toast.error('Failed to convert proposal');
    }
  };

  const handleViewPreview = (proposal) => {
    setSelectedProposal(proposal);
    setShowPreview(true);
  };

  const handleExportPDF = async (proposalId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/proposals/${proposalId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal_${proposalId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleOpenEmailDialog = (proposal) => {
    setSelectedProposal(proposal);
    setEmailData({
      to_email: proposal.email || '',
      subject: `Proposal from ${relatedData.companyInfo?.company_name || 'Tidify Cleaning Services'}`,
      message: `Dear ${proposal.contact_name || 'Client'},\n\nPlease find attached our proposal for janitorial services at ${proposal.company_name}. We appreciate the opportunity to serve your facility.\n\nBest regards`
    });
    setShowEmailDialog(true);
    setShowTemplateLibrary(false);
  };

  const handleSelectTemplate = (templateData) => {
    setEmailData({
      ...emailData,
      subject: templateData.subject,
      message: templateData.message,
      template_id: templateData.template_id
    });
    setShowTemplateLibrary(false);
    toast.success('Template applied');
  };

  const handleSendEmail = async () => {
    if (!selectedProposal) return;
    
    if (!emailData.to_email) {
      toast.error('Please enter a recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      await api(`/api/proposals/${selectedProposal.id}/send-email`, {
        method: 'POST',
        body: JSON.stringify(emailData)
      });
      toast.success(`Proposal sent to ${emailData.to_email}`);
      setShowEmailDialog(false);
      loadProposals();
    } catch (error) {
      toast.error('Failed to send email');
      console.error(error);
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const statusColors = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    sent: 'bg-sky-50 text-sky-700 border-sky-200',
    viewed: 'bg-teal-50 text-teal-800 border-teal-200',
    won: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    accepted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    declined: 'bg-rose-50 text-rose-700 border-rose-200'
  };

  if (showPreview && selectedProposal) {
    return (
      <div className="space-y-6">
        {/* Context Header */}
        <RecordContextHeader
          label="Viewing Proposal"
          company={selectedProposal.company_name}
          facility={selectedProposal.facility_name || relatedData.lead?.company_name}
          status={selectedProposal.status}
          additionalInfo={`$${selectedProposal.monthly_price}/mo`}
        />

        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Proposal Preview</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExportPDF(selectedProposal.id)}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button variant="outline" onClick={() => handleOpenEmailDialog(selectedProposal)}>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </div>
        </div>
        
        <ProposalPreview 
          proposal={selectedProposal}
          lead={relatedData.lead}
          walkthrough={relatedData.walkthrough}
          bid={relatedData.bid}
          companyInfo={relatedData.companyInfo}
        />

        {/* Proposal Attachments */}
        <ProposalAttachments proposalId={selectedProposal.id} />
      </div>
    );
  }

  if (showEmailDialog) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight flex items-center gap-2">
            <Mail className="h-8 w-8" />
            Send Proposal via Email
          </h1>
          <p className="text-muted-foreground">Send proposal to {selectedProposal?.company_name}</p>
        </div>

        {showTemplateLibrary ? (
          <EmailTemplateLibrary 
            proposal={selectedProposal}
            onSelectTemplate={handleSelectTemplate}
          />
        ) : (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowTemplateLibrary(true)}
                >
                  <FileCode className="h-4 w-4 mr-2" />
                  Browse Templates
                </Button>
              </div>

              <div>
                <Label htmlFor="to_email">Recipient Email *</Label>
                <Input
                  id="to_email"
                  type="email"
                  value={emailData.to_email}
                  onChange={(e) => setEmailData({...emailData, to_email: e.target.value})}
                  placeholder="client@company.com"
                  data-testid="email-to-input"
                />
              </div>
              
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  data-testid="email-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={emailData.message}
                  onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                  rows={12}
                  data-testid="email-message-input"
                />
                <p className="text-xs text-muted-foreground mt-1">The proposal PDF will be attached automatically with email tracking enabled</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleSendEmail} disabled={sendingEmail} data-testid="send-email-button">
                  <Mail className="h-4 w-4 mr-2" />
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
                <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Won Prompt */}
      {showWonPrompt && (
        <NextStepPrompt
          title="Congratulations! 🎉"
          message="Proposal won and client created. What's next?"
          primaryAction="Open Client"
          secondaryAction="Continue"
          onPrimaryClick={() => {
            setShowWonPrompt(false);
            window.location.href = '#clients';
          }}
          onSecondaryClick={() => setShowWonPrompt(false)}
        />
      )}

      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Proposals</h1>
        <p className="text-muted-foreground">Manage and track your service proposals</p>
      </div>

      {editingProposal && (
        <Card>
          <CardHeader>
            <CardTitle>Edit Proposal</CardTitle>
            <CardDescription>{editingProposal.company_name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Monthly Price</label>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">$</span>
                <input
                  type="number"
                  value={editFormData.monthly_price || ''}
                  onChange={(e) => setEditFormData({...editFormData, monthly_price: parseFloat(e.target.value)})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="edit-proposal-price-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Services</label>
              <textarea
                value={editFormData.services || ''}
                onChange={(e) => setEditFormData({...editFormData, services: e.target.value})}
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="edit-proposal-services-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <textarea
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                rows={2}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="edit-proposal-notes-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Exclusions</label>
              <textarea
                value={editFormData.exclusions || ''}
                onChange={(e) => setEditFormData({...editFormData, exclusions: e.target.value})}
                rows={2}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="edit-proposal-exclusions-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} data-testid="save-proposal-edit-button">
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => { setEditingProposal(null); setEditFormData({}); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All ({proposals.length})</TabsTrigger>
          <TabsTrigger value="draft">Draft ({proposals.filter(p => p.status === 'draft').length})</TabsTrigger>
          <TabsTrigger value="sent">Sent ({proposals.filter(p => p.status === 'sent' || p.status === 'viewed').length})</TabsTrigger>
          <TabsTrigger value="won">Won ({proposals.filter(p => p.status === 'won' || p.status === 'accepted').length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <ProposalsList 
            proposals={proposals} 
            onView={handleViewPreview}
            onUpdateStatus={handleUpdateStatus}
            onMarkWon={handleMarkWon}
            statusColors={statusColors}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
            onSendEmail={handleOpenEmailDialog}
            bulkActions={bulkActions}
          />
        </TabsContent>

        <TabsContent value="draft" className="mt-6">
          <ProposalsList 
            proposals={proposals.filter(p => p.status === 'draft')} 
            onView={handleViewPreview}
            onUpdateStatus={handleUpdateStatus}
            onMarkWon={handleMarkWon}
            statusColors={statusColors}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
            onSendEmail={handleOpenEmailDialog}
          />
        </TabsContent>

        <TabsContent value="sent" className="mt-6">
          <ProposalsList 
            proposals={proposals.filter(p => p.status === 'sent' || p.status === 'viewed')} 
            onView={handleViewPreview}
            onUpdateStatus={handleUpdateStatus}
            onMarkWon={handleMarkWon}
            statusColors={statusColors}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
            onSendEmail={handleOpenEmailDialog}
          />
        </TabsContent>

        <TabsContent value="won" className="mt-6">
          <ProposalsList 
            proposals={proposals.filter(p => p.status === 'won' || p.status === 'accepted')} 
            onView={handleViewPreview}
            onUpdateStatus={handleUpdateStatus}
            onMarkWon={handleMarkWon}
            statusColors={statusColors}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
            onSendEmail={handleOpenEmailDialog}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProposalsList({ proposals, onView, onUpdateStatus, onMarkWon, statusColors, formatCurrency, formatDate, onDelete, onDuplicate, onEdit, onSendEmail, bulkActions }) {
  if (proposals.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No Proposals Found"
        message="Create proposals from calculated bids on the Bidding page. Proposals allow you to send professional quotes to your potential clients."
        actionLabel="Go to Bidding"
        onActionClick={() => window.location.href = '#bidding'}
      />
    );
  }

  return (
    <div className="space-y-6">
      {bulkActions && bulkActions.selectedCount > 0 && (
        <div className="text-sm text-muted-foreground">
          {bulkActions.selectedCount} proposal(s) selected
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-4">
        {proposals.map((proposal) => (
          <Card key={proposal.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3 flex-1">
                  {bulkActions && (
                    <Checkbox
                      checked={bulkActions.isSelected(proposal.id)}
                      onCheckedChange={() => bulkActions.toggleSelect(proposal.id)}
                      className="mt-1"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{proposal.company_name}</h3>
                        <p className="text-sm text-muted-foreground">{proposal.contact_name}</p>
                      </div>
                      <Badge className={statusColors[proposal.status]}>
                        {proposal.status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Monthly Price</p>
                        <p className="text-lg font-semibold text-brand tabular-nums">{formatCurrency(proposal.monthly_price)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Frequency</p>
                        <p className="text-sm font-medium">{proposal.frequency?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="text-sm">{formatDate(proposal.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Expires</p>
                        <p className="text-sm">{formatDate(proposal.expiration_date)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={() => onView(proposal)} data-testid="view-proposal-button">
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              
              {proposal.status === 'draft' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onEdit(proposal)}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button size="sm" onClick={() => onSendEmail(proposal)} data-testid="send-proposal-email-button">
                    <Mail className="h-4 w-4 mr-1" />
                    Send Email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(proposal.id)}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </>
              )}
              
              {['sent', 'viewed'].includes(proposal.status) && (
                <>
                  <Button size="sm" onClick={() => onSendEmail(proposal)}>
                    <Mail className="h-4 w-4 mr-1" />
                    Resend
                  </Button>
                  <Button size="sm" onClick={() => onMarkWon(proposal.id)} data-testid="mark-won-button">
                    <Check className="h-4 w-4 mr-1" />
                    Mark Won
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onUpdateStatus(proposal.id, 'declined')}>
                    <X className="h-4 w-4 mr-1" />
                    Decline
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onUpdateStatus(proposal.id, 'draft')}>
                    Move to Draft
                  </Button>
                </>
              )}

              {proposal.status === 'declined' && (
                <Button size="sm" variant="outline" onClick={() => onUpdateStatus(proposal.id, 'draft')}>
                  Reopen
                </Button>
              )}

              {proposal.status === 'won' && (
                <Button size="sm" variant="outline" onClick={() => {
                  if (window.confirm('Reopen won proposal? This will not affect the created client.')) {
                    onUpdateStatus(proposal.id, 'draft');
                  }
                }}>
                  Reopen (Advanced)
                </Button>
              )}

              <Button size="sm" variant="ghost" onClick={() => onDuplicate(proposal)}>
                <FileText className="h-4 w-4 mr-1" />
                Duplicate
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      </div>
      
      {bulkActions?.BulkActionBar && bulkActions.BulkActionBar()}
    </div>
  );
}
